/**
 * Абонентское обслуживание по обучению (training-subscription, доработка ТЗ заказчика 05.10.2026).
 *
 * Договор с организацией на срок «с … по …»: срок задают администратор и руководитель в
 * реквизитах клиента (training-subscription-head), отдельной галочки нет — клиент на
 * обслуживании, пока сегодняшняя дата входит в срок (считает сервер, `active`). Клиент видит надпись в разделе «Обучение»; заказ обучения, отправленный в
 * срок договора, получает отметку для команды.
 *
 * Здесь всё, что относится к обслуживанию на обеих сторонах: карточка срока во вкладке
 * «Реквизиты» клиента (команда), надпись для клиента и отметка заказа.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { teamApi, type TrainingSubscription } from '@/lib/api'
import { formatIsoToRu } from '@/lib/date-input'
import { getErrorMessage } from '@/lib/errors'
import { Card } from '@/design-system/components/layout/card'
import { Badge } from '@/design-system/components/primitives/badge'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { DateInput } from '@/design-system/components/primitives/date-input'
import { Hint } from '@/design-system/components/primitives/text'
import { Alert } from '@/design-system/components/feedback/alert'

/** Отметка заказа — формулировка заказчика («чтобы админ видел»). */
export const TRAINING_SUBSCRIPTION_ORDER_LABEL = 'Заказ по абонентскому обслуживанию по обучению'

/** «с 01.01.2026 по 31.12.2026»; пусто, если срок не задан. */
export function formatTrainingSubscriptionPeriod(sub: TrainingSubscription): string {
  const from = formatIsoToRu(sub.from)
  const to = formatIsoToRu(sub.to)
  return from && to ? `с ${from} по ${to}` : ''
}

/**
 * Отметка заказа по абонентскому обслуживанию — только на стороне команды. В таблицах (`short`)
 * подпись сокращена, полная формулировка остаётся в подсказке и для вспомогательных технологий.
 */
export function TrainingSubscriptionOrderBadge({
  short = false,
  className,
}: {
  short?: boolean
  className?: string
}) {
  return (
    <Badge
      variant="blue"
      className={className}
      title={short ? TRAINING_SUBSCRIPTION_ORDER_LABEL : undefined}
    >
      {short ? 'Абонентское обслуживание' : TRAINING_SUBSCRIPTION_ORDER_LABEL}
    </Badge>
  )
}

/** Надпись в разделе «Обучение» клиента: показывается, только пока обслуживание активно. */
export function TrainingSubscriptionNotice({
  subscription,
  className,
}: {
  subscription: TrainingSubscription | null
  className?: string
}) {
  if (!subscription?.active) return null
  return (
    <Alert variant="info" role="status" className={className}>
      Активировано абонентское обслуживание по обучению{' '}
      {formatTrainingSubscriptionPeriod(subscription)}
    </Alert>
  )
}

function statusBadge(sub: TrainingSubscription) {
  if (sub.active) return <Badge variant="green">Действует</Badge>
  if (sub.from && sub.to) return <Badge variant="amber">Вне срока договора</Badge>
  return <Badge variant="gray">Не оформлено</Badge>
}

/** Проверка срока до отправки — те же условия, что на сервере (ISO-даты сравнимы как строки). */
function validatePeriod(from: string, to: string): string | null {
  if (!from && !to) return null
  if (!from) return 'Укажите дату начала договора'
  if (!to) return 'Укажите дату окончания договора'
  if (from > to) return 'Дата окончания раньше даты начала договора'
  return null
}

/**
 * Карточка срока договора во вкладке «Реквизиты» клиента-организации (команда). Правят
 * администратор и руководитель (`canEdit`); сотрудник видит срок без полей ввода.
 */
export function TrainingSubscriptionCard({
  cid,
  subscription,
  canEdit,
  onSaved,
}: {
  cid: string
  subscription: TrainingSubscription
  canEdit: boolean
  /** Свежий срок после сохранения — владелец карточки клиента обновляет им своё состояние. */
  onSaved: (next: TrainingSubscription) => void
}) {
  const [from, setFrom] = useState(subscription.from)
  const [to, setTo] = useState(subscription.to)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setFrom(subscription.from)
    setTo(subscription.to)
  }, [subscription.from, subscription.to])

  const unchanged = from === subscription.from && to === subscription.to

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (saving) return
    setSaved(false)
    const invalid = validatePeriod(from, to)
    if (invalid) {
      setError(invalid)
      return
    }
    setError(null)
    setSaving(true)
    try {
      const res = await teamApi.setTrainingSubscription(cid, { from, to })
      onSaved(res.trainingSubscription)
      setSaved(true)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить срок договора'))
    } finally {
      setSaving(false)
    }
  }

  const period = formatTrainingSubscriptionPeriod(subscription)

  return (
    <Card header="Абонентское обслуживание по обучению" className="mt-4">
      <div className="flex flex-wrap items-center gap-2.5">
        {statusBadge(subscription)}
        <span className="text-13 text-txt-secondary">
          {period ? `Договор ${period}` : 'Срок договора не задан'}
        </span>
      </div>
      <Hint className="mt-2">
        Клиент на обслуживании, пока сегодняшняя дата входит в срок договора. Он видит это в разделе
        «Обучение», а его заказы обучения, отправленные в срок договора, получают отметку «
        {TRAINING_SUBSCRIPTION_ORDER_LABEL}».
      </Hint>
      {canEdit ? (
        <form onSubmit={submit} noValidate>
          {error ? (
            <Alert variant="danger" className="mt-3">
              {error}
            </Alert>
          ) : null}
          {/* Границы календаря не связываем между полями: при продлении договора дата начала
              нового срока позже прежней даты окончания — порядок дат проверяется при сохранении. */}
          <div className="grid max-w-md grid-cols-1 gap-x-4 nw:grid-cols-2">
            <Field label="Договор с">
              {(props) => (
                <DateInput
                  {...props}
                  value={from}
                  onChange={(next) => {
                    setSaved(false)
                    setFrom(next)
                  }}
                />
              )}
            </Field>
            <Field label="по">
              {(props) => (
                <DateInput
                  {...props}
                  value={to}
                  onChange={(next) => {
                    setSaved(false)
                    setTo(next)
                  }}
                />
              )}
            </Field>
          </div>
          <Hint>Чтобы снять клиента с обслуживания, очистите обе даты и сохраните.</Hint>
          <div className="mt-3 flex items-center gap-3">
            <Button type="submit" size="sm" disabled={saving || unchanged}>
              {saving ? 'Сохранение…' : 'Сохранить'}
            </Button>
            {saved ? (
              <span role="status" className="text-13 font-medium text-green">
                Сохранено
              </span>
            ) : null}
          </div>
        </form>
      ) : (
        <Hint className="mt-2">Срок договора задают администратор и руководитель.</Hint>
      )}
    </Card>
  )
}
